import { InboxOutlined } from "@ant-design/icons";
import { useCIQLazyQuery, useCIQMutation } from "hooks/ciq-gql-hooks";
import {
  Button,
  message,
  Modal,
  Progress,
  Skeleton,
  Tooltip,
  Upload
} from "antd";
import { RcFile, UploadFile, UploadProps } from "antd/lib/upload";
import Dragger from "antd/lib/upload/Dragger";
import { ErrorMessages, SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { noAuthClient } from "services/axios";
import { generateS3URL } from "services/file-mgmt";
import { MUTATION_INSERT_SPECIFICATION } from "services/graphQL/mutations";
import { extractUploadDrawingAPI } from "../auth/extraction-auth";
import InProgressPanel from "./in-progress-panel";
import { ExtractSpecCardItem } from "../model";
import { AI_PROJECT_LIMITS } from "services/graphQL/queries";
import ExtractionUploadErrorAlert, {
  TExtractionUploadErrorMessages
} from "./error-message/upload-error-message-box";

const AI_EXTRACTION_DISCLAIMER_SUMMARY =
  "This application uses AI to extract materials and link them to schedule activities.  It may make mistakes.";

export function ExtractionLimitConsumptionTracker({
  totalPages,
  processedPages
}: {
  totalPages: number | undefined;
  processedPages: number | undefined;
}): JSX.Element {
  // check for undefined values
  if (totalPages === undefined || processedPages === undefined) {
    return <div></div>;
  }
  const percentConsumption = (processedPages / totalPages) * 100;
  const pagesRemaining = totalPages - processedPages;
  const pagesRemainingTooltip =
    pagesRemaining > 0
      ? `${pagesRemaining} pages remaining`
      : "No pages remaining";

  const isNearLimit = percentConsumption >= 80;

  return (
    <div className="border border-solid border-ciq-gray-300 rounded-lg p-2 space-y-2">
      <div className="text-ciq-black-a88 font-semibold">
        Page Processing Limit
      </div>
      <Tooltip title={pagesRemainingTooltip}>
        <Progress
          percent={percentConsumption}
          showInfo={false}
          status={isNearLimit ? "exception" : "active"}
          strokeColor={isNearLimit ? undefined : "#722ED1"}
        />
      </Tooltip>
      <div className="text-xs text-black text-opacity-90">
        <span className="font-semibold">
          {processedPages} / {totalPages}
        </span>{" "}
        <span className="text-ciq-black/65">Pages processed</span>
      </div>
    </div>
  );
}

export default function ImportExtractionDrawing({
  openDrawer,
  onClose,
  extractionHistoryData,
  refetchExtractionHistoryData
}: {
  openDrawer: boolean;
  onClose: () => void;
  extractionHistoryData?: Array<ExtractSpecCardItem> | null;
  refetchExtractionHistoryData: () => void;
}) {
  const { tokenRetrievalState, gqlClientForProject, projectDetails } =
    useContext(ProjectContext);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [unsupportedFileSelected, setUnsupportedFileSelected] = useState(false);
  const [aiProjectLimitsData, setAiProjectLimitsData] = useState<any>(null);

  const [getAiProjectLimits, { loading: aiProjectLimitsLoading }] =
    useCIQLazyQuery(AI_PROJECT_LIMITS, {
      token: tokenRetrievalState?.token
    });

  const fetchAiProjectLimits = useCallback(async () => {
    const aiProjectLimitsDataResponse = await getAiProjectLimits();
    setAiProjectLimitsData(aiProjectLimitsDataResponse?.data?.data);
  }, [getAiProjectLimits]);

  useEffect(() => {
    fetchAiProjectLimits();
  }, [fetchAiProjectLimits]);

  const extractionLimits = useMemo(() => {
    return {
      totalPages: aiProjectLimitsData?.ai_project_limits[0]?.monthly_page_limit,
      processedPages: aiProjectLimitsData?.ai_project_usage[0]?.pages_processed
    };
  }, [aiProjectLimitsData]);

  const [insertSpecification] = useCIQMutation(MUTATION_INSERT_SPECIFICATION, {
    client: gqlClientForProject
  });

  const [loading, setLoading] = useState(false);
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(false);
  const [uploadErrorMessages, setUploadErrorMessages] =
    useState<TExtractionUploadErrorMessages>();

  const props: UploadProps = {
    accept: "application/pdf",
    style: { borderColor: unsupportedFileSelected === true ? "red" : "" },
    multiple: false,
    fileList,
    onChange: () => {},
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      if (e.dataTransfer.files.length === 0) {
        return false;
      }

      const file = e.dataTransfer.files[0];
      if (file.type !== "application/pdf") {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error(ErrorMessages.specUploads.pdfSelectionOnly);
        return false;
      }

      setUnsupportedFileSelected(false);
      return true;
    },
    beforeUpload: (file: RcFile) => {
      if (file.type !== "application/pdf") {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error(ErrorMessages.specUploads.pdfSelectionOnly);
        return Upload.LIST_IGNORE;
      }
      setUnsupportedFileSelected(false);
      setFileList([file]);
      return false;
    },
    showUploadList: {
      showDownloadIcon: false,
      showPreviewIcon: false
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    const selectedFile: any = fileList[0];

    const specPayload: any = {
      specification: {
        type: "DRAWING",
        title: selectedFile.name
      }
    };

    try {
      const insertSpecResponse = await insertSpecification({
        variables: { ...specPayload }
      });

      if (
        insertSpecResponse.errors ||
        !insertSpecResponse?.data?.insert_specifications_one?.id
      ) {
        message.error(
          insertSpecResponse.errors?.[0]?.message || "Import failed"
        );
        setFileList([]);
        setLoading(false);
        return;
      }

      const specificationId =
        insertSpecResponse.data.insert_specifications_one.id;

      const signedUrlRespose: any = await generateS3URL(
        {
          feature: "DRAWING",
          feature_id: specificationId,
          file_name: selectedFile.name
        },
        tokenRetrievalState.token
      );

      await noAuthClient.put(
        `${signedUrlRespose.data.success.url}`,
        selectedFile
      );

      const res = await extractUploadDrawingAPI({
        specificationId,
        signedUrl: signedUrlRespose.data.success.url,
        projectId: projectDetails?.id ?? "",
        projectToken: tokenRetrievalState.token
      });

      if (res.data) {
        refetchExtractionHistoryData();
        fetchAiProjectLimits();
        setTimeout(() => {
          setFileList([]);
          setLoading(false);
        }, 1000);
        return;
      }
      message.error("Failed to trigger extraction process");
    } catch (ex: any) {
      const data = ex.response?.data;
      const errorInfo: TExtractionUploadErrorMessages = {
        error_type: data?.error_type,
        error_code: data?.error_code,
        error: data?.error,
        id: data?.id || data?.existing_specification_id || "",
        fileName: data?.existing_specification?.title || "",
        current_usage: data?.current_usage,
        total_limit: data?.total_limit,
        remaining_pages: data?.remaining_pages,
        percent_consumed: data?.percent_consumed,
        pages_to_process: data?.pages_to_process
      };
      setUploadErrorMessages(errorInfo);
    }
    setFileList([]);
    setLoading(false);
  };

  return (
    <Modal
      className="custom-drawer"
      title="Extract from drawings"
      width={420}
      style={SidepanelStyles.style}
      styles={{ body: { height: "calc(100vh - 58px)" } }}
      footer={null}
      open={openDrawer}
      mask={{ closable: false }}
      closable={!loading}
      keyboard={!loading}
      onCancel={() => {
        onClose();
      }}
      destroyOnHidden
      data-testid="index-modal-extract-material-log"
    >
      <div className="flex flex-col h-full w-full px-3">
        <div className="space-y-4 w-full">
          {!aiProjectLimitsLoading && aiProjectLimitsData ? (
            <ExtractionLimitConsumptionTracker
              totalPages={extractionLimits?.totalPages}
              processedPages={extractionLimits?.processedPages}
            />
          ) : (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          )}

          {uploadErrorMessages && (
            <div className="flex flex-col space-y-2">
              <ExtractionUploadErrorAlert
                key={uploadErrorMessages.id}
                errorInfo={uploadErrorMessages}
                onClose={() => setUploadErrorMessages(undefined)}
              />
            </div>
          )}

          <div className="w-full">
            <Dragger
              {...props}
              disabled={loading}
              data-testid="index-dragger-upload"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              {fileList.length > 0 ? (
                <div className="flex px-3 justify-center items-center">
                  <Tooltip title={fileList[0].name}>
                    <p
                      className="ant-upload-text truncate max-w-[330px]"
                      data-testid="index-uploaded-filename"
                    >
                      {fileList[0].name}
                    </p>
                  </Tooltip>
                </div>
              ) : (
                <div>
                  <p
                    className="ant-upload-text"
                    data-testid="index-upload-instruction"
                  >
                    Click or drag file to upload.
                  </p>
                  <p className="text-ciq-gray-800 text-opacity-80 text-xs">
                    Only PDF files are supported
                  </p>
                </div>
              )}
            </Dragger>
          </div>

          {fileList.length > 0 && (
            <div className="flex justify-center">
              <Button
                type="primary"
                disabled={!fileList.length || loading}
                onClick={handleUpload}
                loading={loading}
                data-testid="index-button-extract"
              >
                Start Extraction
              </Button>
            </div>
          )}
          <InProgressPanel data={extractionHistoryData} />
        </div>
        <div
          className="mt-auto -mb-6 pt-4 text-xs text-black text-opacity-50 leading-relaxed text-justify"
          data-testid="index-ai-disclaimer"
        >
          {isDisclaimerExpanded ? (
            <div className="text-black text-opacity-40">
              <span className="mb-0 font-semibold ">
                {AI_EXTRACTION_DISCLAIMER_SUMMARY}
              </span>{" "}
              AI models are subject to limitations and may produce incomplete,
              unlinked, or inaccurate outputs. The user assumes all risk and
              liability for verifying the accuracy of the extracted quantities
              and activity links against official engineering drawings and
              project plans prior to any procurement, scheduling, or operational
              use.{" "}
              <Button
                type="link"
                size="small"
                className="p-0 border-0 bg-transparent font-semibold text-xs cursor-pointer"
                onClick={() => setIsDisclaimerExpanded(false)}
                data-testid="index-ai-disclaimer-view-less"
              >
                View Less
              </Button>
            </div>
          ) : (
            <div className="mb-0">
              <span className="font-semibold text-black text-opacity-40">
                {AI_EXTRACTION_DISCLAIMER_SUMMARY}
              </span>{" "}
              <Button
                type="link"
                size="small"
                className="p-0 border-0 bg-transparent font-semibold text-xs cursor-pointer"
                onClick={() => setIsDisclaimerExpanded(true)}
                data-testid="index-ai-disclaimer-view-more"
              >
                View More
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
